import { MainBadge } from './badge.js';

/**
 * 主副点计算服务
 *
 * @public
 */
class BadgeService {
    constructor(persistStorage) {
        this.persistStorage = persistStorage;
        this.badgeList = new Map();
        this.subscribers = [];
    }
    /**
     * 设置当前红点前缀，例如用户名，从而使得多个用户红点各不干扰
     * @param prefix - 前缀内容
     */
    setPrefix(prefix) {
        var _a;
        (_a = this.persistStorage) === null || _a === void 0 ? void 0 : _a.setPrefix(prefix);
    }
    /**
     * 显示一个红点
     *
     * @param position - 位置类型
     * @param options - 红点配置
     *
     * @returns 返回一个主点，可向其附加副点
     */
    showBadgeOn(position, options) {
        const key = `${position}-${options.key}`;
        const now = Date.now();
        const info = Object.assign(Object.assign({ version: 1 }, options), { position });
        const badge = new MainBadge(info);
        if (options.expire > now && !this.isRemoved(info)) {
            this.badgeList.set(key, badge);
            badge.onUpdate = () => {
                this.updateBadge();
            };
            this.updateBadge();
        }
        return badge;
    }
    /**
     * 移除一个红点，其可能是主点，亦可能是副点。
     * 若移除的点是一个主点，则主点相关的所有副点都会移除
     * 若移除的点是一个副点，则仅该副点会被移除，且不会被记录
     *
     * @param position - 位置类型
     * @param key - 红点标识
     */
    removeBadgeOn(position, key) {
        const composedKey = `${position}-${key}`;
        const badge = this.badgeList.get(composedKey);
        let shouldUpdate = false;
        if (badge) {
            this.badgeList.delete(composedKey);
            this.setPersistStorage(badge.info);
            shouldUpdate = true;
            if (badge.info.group) {
                this.badgeList.forEach((mainBadge) => {
                    if (mainBadge.info.group === badge.info.group) {
                        this.badgeList.delete(`${mainBadge.info.position}-${mainBadge.info.key}`);
                        this.setPersistStorage(mainBadge.info);
                        shouldUpdate = true;
                    }
                });
            }
        }
        this.badgeList.forEach((mainBadge) => {
            shouldUpdate = mainBadge.removeRelatedBadge(position, key) || shouldUpdate;
        });
        if (shouldUpdate) {
            this.updateBadge();
        }
    }
    // implemention
    getBadgesOn(position, key) {
        var _a;
        if (key !== undefined) {
            const composedKey = `${position}-${key}`;
            return (_a = this.badgeList.get(composedKey)) !== null && _a !== void 0 ? _a : null;
        }
        const res = [];
        for (const [k, badge] of this.badgeList.entries()) {
            if (k.startsWith(`${position}-`)) {
                res.push(badge);
            }
        }
        return res.length ? res : null;
    }
    /**
     * 查询特定位置上是否存在红点
     *
     * @param position - 位置类型
     * @param key - 红点标识
     */
    hasBadgesOn(position, key) {
        for (const k of this.badgeList.keys()) {
            if (k.startsWith(`${position}-${key !== null && key !== void 0 ? key : ''}`)) {
                return true;
            }
        }
        return false;
    }
    /**
     * 监听主副点变化
     *
     * @param callback - 监听器
     */
    subscribe(callback) {
        this.subscribers.push(callback);
    }
    /**
     * 移除监听器
     *
     * @param callback - 监听器
     */
    unsubscribe(callback) {
        this.subscribers = this.subscribers.filter(subscriber => subscriber !== callback);
    }
    /**
     * 清除所有红点，该行为不会对持久化存储生效
     */
    clear() {
        this.badgeList.clear();
        this.updateBadge();
    }
    isRemoved(badgeInfo) {
        if (this.persistStorage) {
            return this.persistStorage.isRemoved(badgeInfo);
        }
        return false;
    }
    setPersistStorage(badgeInfo) {
        if (this.persistStorage) {
            this.persistStorage.setRemoved(badgeInfo);
        }
    }
    updateBadge() {
        const activeBadges = new Map();
        this.badgeList.forEach((badge, key) => {
            activeBadges.set(key, badge.info);
            badge.relatedBadges.forEach((b) => {
                const composedKey = `${b.position}-${b.key}`;
                if (!activeBadges.has(composedKey)) {
                    activeBadges.set(composedKey, Object.assign(Object.assign({}, b), { expire: badge.info.expire }));
                }
            });
        });
        const badges = [...activeBadges.values()];
        this.subscribers.forEach(() => {
            this.subscribers.forEach(callback => callback(badges));
        });
    }
    /**
     * 获取所有主点
     */
    getMainBadges() {
        return Array.from(this.badgeList.values());
    }
}

export { BadgeService };
//# sourceMappingURL=badge-service.js.map
